import React, { PropsWithChildren, Children } from "react";

import { css } from "@compiled/react";

import { token } from "@atlaskit/tokens";

import { isLocalizableHref, useLocalizedHref } from "@/libraries/web-framework";

import { DataEventProps } from "../../../../wpl/foundations/src/index";
import wacToken from "../../../foundations/token/src/wac-token";

//Types
export type LinkType =
    | "primary"
    | "external"
    | "video"
    | "inline"
    | "modal"
    | "anchor"
    | "download";

export type BgColors = "bgWhite" | "bgColored" | "bgBlackBlue";

interface LinkColors {
    bgWhite: Colors;
    bgColored: Colors;
    bgBlackBlue: Colors;
}

interface Colors {
    defaultColor: string;
    hoverColor: string;
    visitedColor: string;
    visitedHoverColor: string;
}

export type LinkSize = "sm" | "md" | "lg";

export interface LinkProps {
    text: string;
    href: string;
    type?: LinkType;
    size?: LinkSize;
    bgColor?: BgColors;
    onClick?: (e: React.MouseEvent) => void;
    className?: string;
    id?: string;
    ariaLabel?: string;
    ariaLabelledby?: string;
    dataEventProps?: DataEventProps;
    isStyleOnly?: boolean;
    anchorProps?: React.AnchorHTMLAttributes<HTMLAnchorElement> &
        Record<string, unknown>;
    ignoreHref?: boolean;
    /** Whether to show the arrow icon. Defaults to true. */
    showArrow?: boolean;
    /** Whether to show the underline. Defaults to true. */
    showUnderline?: boolean;
}

//CSS
export const linkColors: LinkColors = {
    bgWhite: {
        defaultColor: wacToken("color.extended.blue700"),
        hoverColor: wacToken("color.extended.blue800"),
        visitedColor: wacToken("color.extended.purple800"),
        visitedHoverColor: wacToken("color.extended.purple900")
    },
    bgBlackBlue: {
        defaultColor: wacToken("color.neutral.light0"),
        hoverColor: wacToken("color.extended.blue300"),
        visitedColor: wacToken("color.extended.purple300"),
        visitedHoverColor: wacToken("color.extended.purple600")
    },
    bgColored: {
        defaultColor: wacToken("color.primary.black"),
        hoverColor: wacToken("color.primary.black"),
        visitedColor: wacToken("color.primary.black"),
        visitedHoverColor: wacToken("color.primary.black")
    }
};

//Functions
const Icon = (type: LinkType) => {
    if (type === "primary") {
        return (
            <svg
                css={css({
                    margin: `${token("space.050")} ${token(
                        "space.0"
                    )} 1px ${token("space.050")}`,
                    transition: "transform 0.25s ease-in-out"
                })}
                width="11"
                height="8"
                viewBox="0 0 11 8"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
                aria-hidden="true"
            >
                <path
                    fillRule="evenodd"
                    clipRule="evenodd"
                    d="M7.40796 0L6.32831 1.2021L8.07854 3.1497H0.763182C0.341906 3.1497 0 3.53038 0 3.99943C0 4.46849 0.341906 4.84917 0.763182 4.84917H8.07854L6.32831 6.7979L7.40796 8L11 3.99943L7.40796 0Z"
                />
            </svg>
        );
    }
    if (type === "external") {
        return (
            <svg
                css={css({
                    margin: `${token("space.050")} ${token("space.0")} ${token(
                        "space.025"
                    )} ${token("space.050")}`
                })}
                width="7"
                height="8"
                viewBox="0 0 7 8"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
                aria-hidden="true"
            >
                <path
                    fillRule="evenodd"
                    clipRule="evenodd"
                    d="M2.04815 0.5V1.98788H4.46059L0.217983 6.22935C-0.0726609 6.51998 -0.0726609 6.99114 0.217983 7.28178C0.363801 7.4266 0.553265 7.5 0.744713 7.5C0.935169 7.5 1.12463 7.4266 1.27045 7.28178L5.51206 3.04031V5.45168H7V0.5H2.04815Z"
                />
            </svg>
        );
    }
    if (type === "video") {
        return (
            <svg
                css={css({
                    marginInlineEnd: `${token("space.050")}`
                })}
                width="14"
                height="14"
                viewBox="0 0 14 14"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
                aria-hidden="true"
            >
                <path
                    fillRule="evenodd"
                    clipRule="evenodd"
                    d="M7 14C10.866 14 14 10.866 14 7C14 3.13401 10.866 0 7 0C3.13401 0 0 3.13401 0 7C0 10.866 3.13401 14 7 14ZM10.3561 7.10299L4.79449 4.21918V9.78082L10.3561 7.10299Z"
                />
            </svg>
        );
    }
    if (type === "download") {
        return (
            <svg
                css={css({
                    marginInlineStart: `${token("space.050")}`
                })}
                width="14"
                height="14"
                viewBox="0 0 52 52"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
                aria-hidden="true"
            >
                <path
                    fillRule="evenodd"
                    clipRule="evenodd"
                    d="M48.5,31h-3c-0.8,0-1.5,0.7-1.5,1.5v10c0,0.8-0.7,1.5-1.5,1.5h-33C8.7,44,8,43.3,8,42.5v-10
		        C8,31.7,7.3,31,6.5,31h-3C2.7,31,2,31.7,2,32.5V46c0,2.2,1.8,4,4,4h40c2.2,0,4-1.8,4-4V32.5C50,31.7,49.3,31,48.5,31z"
                />
                <path
                    fillRule="evenodd"
                    clipRule="evenodd"
                    className="download-arrow"
                    d="M25,37.6c0.6,0.6,1.5,0.6,2.1,0l13.5-13.5c0.6-0.6,0.6-1.5,0-2.1l-2.1-2.1c-0.6-0.6-1.5-0.6-2.1,0l-5.6,5.6
                c-0.6,0.6-1.7,0.2-1.7-0.7V3.5C29,2.7,28.2,2,27.5,2h-3C23.7,2,23,2.7,23,3.5v21.2c0,0.9-1.1,1.3-1.7,0.7l-5.6-5.6
                c-0.6-0.6-1.5-0.6-2.1,0L11.5,22c-0.6,0.6-0.6,1.5,0,2.1L25,37.6z"
                />
            </svg>
        );
    }
    return;
};

const formatLinkText = (text: string, type: LinkType, showArrow = true) => {
    if (type === "video") {
        return (
            <React.Fragment>
                {Icon(type)}
                <span>{text}</span>
            </React.Fragment>
        );
    }
    return (
        <React.Fragment>
            <span>
                {text}
                {showArrow && Icon(type)}
            </span>
        </React.Fragment>
    );
};

const isSignInUrl = (href: string) => {
    const baseUrl = process.env["NEXT_PUBLIC_IDENTITY_BASE_URL"];
    const stargateBaseUrl = process.env["NEXT_PUBLIC_API_PRIVATE_BASE_URL"];
    const continueUrl = stargateBaseUrl + "/start/authredirect";
    const signInUrl = `${baseUrl}/login?continue=${encodeURIComponent(
        continueUrl
    )}`;

    // Check if href matches sign-in URL with or without application parameter
    return href === signInUrl || href.startsWith(signInUrl + "&application=");
};

const isHyperlinkRef = (href: string) => {
    return href.startsWith("#");
};

const isNonSigninOrWacUrl = (href: string) => {
    return (
        !isLocalizableHref(href) && !isSignInUrl(href) && !isHyperlinkRef(href)
    );
};

export const ConditionalWrapper = (props: PropsWithChildren<LinkProps>) => {
    const children = Children.toArray(props.children);
    if (props.type === "inline") {
        return (
            <React.Fragment>{children.map((child) => child)}</React.Fragment>
        );
    }
    return <div className="link-wrapper">{children.map((child) => child)}</div>;
};

export const Link = ({
    text,
    href,
    type = "primary",
    size,
    bgColor,
    onClick,
    className,
    id,
    ariaLabel,
    ariaLabelledby,
    dataEventProps,
    isStyleOnly,
    anchorProps = {},
    ignoreHref,
    showArrow = true,
    showUnderline = true
}: // eslint-disable-next-line complexity
LinkProps) => {
    const bg = bgColor ? bgColor : "bgWhite";

    const colorDefault = linkColors[bg].defaultColor;
    const colorHover = linkColors[bg].hoverColor;
    const colorVisited = linkColors[bg].visitedColor;
    const colorVisitedHover = linkColors[bg].visitedHoverColor;

    const linkStyles = [
        type === "modal" &&
            css({
                textDecorationLine: "underline",
                textDecorationStyle: "dashed",
                textDecorationThickness: "1px",
                textUnderlineOffset: `${token("space.025")}`
            }),
        type === "inline" &&
            showUnderline &&
            css({
                textDecoration: "underline",
                textUnderlineOffset: `${token("space.025")}`
            }),
        type === "inline" && !showUnderline && css({ textDecoration: "none" }),
        type !== "modal" &&
            type !== "inline" &&
            css({ textDecoration: "none" }),
        type === "primary" &&
            css({
                "&:hover": {
                    textShadow: "none",
                    // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
                    svg: {
                        transform: `translateX(${token("space.050")})`
                    }
                }
            }),
        type === "primary" &&
            size === "lg" &&
            css({
                // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
                svg: {
                    height: "10px",
                    width: "auto"
                }
            }),
        type === "external" &&
            css({
                "&:hover": {
                    textShadow: "none",
                    // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
                    svg: {
                        transform: `translate(${token("space.025")}, -2px)`
                    }
                }
            }),
        type === "download" &&
            css({
                "&:hover": {
                    textShadow: "none"
                },
                // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
                "&:hover .download-arrow": {
                    textShadow: "none",
                    transform: `translateY(${token("space.025")})`,
                    transition: "transform 0.25s ease-in-out"
                }
            }),
        bg === "bgColored" &&
            css({
                // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
                "&:visited span, &:visited > svg": {
                    "opacity": 0.8,
                    "mix-blend-mode": "multiply"
                },
                // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
                "&:hover span, &:hover > svg": {
                    "opacity": 0.6,
                    "mix-blend-mode": "multiply"
                },
                // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
                "&:visited:hover span, &:visited:hover > svg": {
                    "opacity": 0.5,
                    "mix-blend-mode": "multiply"
                }
            }),
        css({
            "alignItems": "center",
            "display": "inline-flex",
            "fontSize": "inherit",
            "color": colorDefault,
            "&:visited": {
                color: colorVisited
            },
            "&:hover": {
                color: colorHover,
                textShadow: ""
            },
            "&:visited:hover": {
                color: colorVisitedHover
            },
            // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
            "svg, path": {
                fill: "currentColor"
            },
            // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
            "svg": {
                transition: "transform 0.25s ease-in-out"
            },
            "&:focus-visible": {
                border: "none",
                borderRadius: "8px",
                outlineStyle: "solid",
                outlineOffset: "2px",
                outlineWidth: "2px",
                outlineColor:
                    bg === "bgWhite"
                        ? wacToken("color.primary.blue")
                        : wacToken("color.neutral.light0")
            },
            "&:visited:focus-visible": {
                color: colorDefault
            }
        })
    ];

    const localizedHref = useLocalizedHref(href);

    return (
        <ConditionalWrapper type={type} text={text} href={href}>
            {!isStyleOnly && (
                <a
                    className={className + (size ? ` link-${size}` : "")}
                    href={ignoreHref ? undefined : localizedHref}
                    download={type === "download" ? "" : undefined}
                    target={
                        type === "external" ||
                        /* redirecting to new tab if the href is not on WAC and not a Signin link */
                        isNonSigninOrWacUrl(href)
                            ? "_blank"
                            : ""
                    }
                    rel={
                        type === "external" || isNonSigninOrWacUrl(href)
                            ? "noreferrer"
                            : ""
                    }
                    css={linkStyles}
                    id={id}
                    aria-label={ariaLabel}
                    aria-labelledby={ariaLabelledby}
                    onClick={onClick}
                    {...dataEventProps}
                    {...anchorProps}
                >
                    {formatLinkText(text, type, showArrow)}
                </a>
            )}
            {isStyleOnly && (
                <span className={className} css={linkStyles}>
                    {formatLinkText(text, type, showArrow)}
                </span>
            )}
        </ConditionalWrapper>
    );
};
